/**
 * cancelFeesViewModel — `(reduxState, raw) → props de <CancelFeesPage>`.
 *
 * Patron B : `GET /api/v1/subscription/cancellation-fees` n'a aucun cas SUCCESS de réducteur, la
 * réponse ne va donc jamais dans `state.data`. Elle vit dans `useCancelFeesData`, qui la passe
 * ici — le view-model reste pur et synchrone.
 *
 * Il lit deux choses du store : la tranche d'appel, pour l'erreur (`state.api.getCancellationFees`),
 * et l'utilisateur courant, que Calendly pré-remplit (legacy `CancelFeesScreen.js:464-466`).
 * Le chargement, lui, appartient au hook : après un succès la tranche est mise au repos avant que
 * `setRaw` ne repeigne, et `request === false` sans frais se lirait « terminé et vide ».
 */
import { createSelector } from "reselect"

import {
    toUICancelFees,
    type ApiCancellationFees,
} from "../../../adapters/parent/SubscriptionCancel/subscriptionCancelAdapter"
import { type UICancelFees } from "../../../ui/types"
import { selectDisplayedStudent } from "../../shared/displayedStudent"

export type CancelFeesViewModel = {
    fees: UICancelFees | null
    /** Erreur bloquante — seulement quand il n'y a rien à montrer. */
    error: unknown | null
    /** Blob brut passé à `openCalendly` — ce n'est pas une UIType. */
    calendlySubject: unknown
}

/** Redux root is still untyped in this app — keep the VM boundary explicit. */
/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
type ReduxState = any

export const cancelFeesViewModel = createSelector(
    [
        (state: ReduxState) => state?.api?.getCancellationFees?.error ?? null,
        (state: ReduxState) => selectDisplayedStudent(state),
        (_state: ReduxState, raw: ApiCancellationFees | null) => raw,
    ],
    (error, calendlySubject, raw): CancelFeesViewModel => {
        const fees = toUICancelFees(raw)

        return {
            fees,
            error: fees != null ? null : error,
            calendlySubject,
        }
    }
)
